"use client";

/**
 * useParallax
 * @description 상자 안의 그림이 스크롤보다 느리게 따라온다 — 커버 사진이 살아 있는 느낌.
 *   그림을 조금 키워 두고(`scale`) 스크롤에 맞춰 세로로 민다. 상자가 잘라 주므로 가장자리가
 *   비지 않는다. 움직임을 줄인 설정에서는 없다.
 * @example
 * const ref = useParallax<HTMLDivElement>();
 * <div ref={ref} className="overflow-hidden">…<img /></div>
 */
import { useRef, type RefObject } from "react";

import { gsap, useGSAP } from "@/shared/lib/gsap";

/** 스크롤 동안 그림이 밀리는 거리 — 상자 높이의 % */
const SHIFT_PERCENT = 14;
/** 밀린 만큼 비지 않도록 키워 두는 배율 */
const SCALE = 1.16;

export function useParallax<E extends HTMLElement>(): RefObject<E | null> {
  const ref = useRef<E>(null);

  useGSAP(
    () => {
      const el = ref.current;
      if (!el) return;
      const mm = gsap.matchMedia();
      mm.add("(prefers-reduced-motion: no-preference)", () => {
        const images = el.querySelectorAll("img");
        gsap.set(images, { scale: SCALE, transformOrigin: "center" });
        gsap.fromTo(
          images,
          { yPercent: -SHIFT_PERCENT / 2 },
          {
            yPercent: SHIFT_PERCENT / 2,
            ease: "none",
            scrollTrigger: {
              trigger: el,
              start: "top bottom",
              end: "bottom top",
              scrub: true,
            },
          },
        );
      });
      return () => mm.revert();
    },
    { scope: ref },
  );

  return ref;
}
